/**
 * useOsSession — OS-INV-10
 *
 * Manages the PIN-based device session token for the OS mobile flow.
 *
 * Storage model:
 *   - `os_session_token`   → the raw 64-char hex token (returned by POST /api/os/session)
 *   - `os_session_expires` → ISO-8601 string (expiresAt from the API response)
 *   - `os_device_id`       → stable UUID persisted forever; identifies this browser/device
 *
 * Expiry: the server issues 1-day tokens. On every check we compare the stored
 * expiresAt against Date.now(). A 30-second buffer ensures we don't send an
 * almost-expired token that the server will immediately reject.
 *
 * The hook is side-effect free: it only reads/writes localStorage and derives
 * state from it. Actual POST /api/os/session is handled by the PinEntry page.
 */

import { useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

const TOKEN_KEY   = 'os_session_token';
const EXPIRES_KEY = 'os_session_expires';
const DEVICE_KEY  = 'os_device_id';
const EXPIRY_BUFFER_MS = 30_000; // 30s safety margin

export interface OsSessionState {
  /** The current session token, or null if none / expired. */
  token: string | null;
  /** Whether a valid (non-expired) session token exists. */
  isAuthenticated: boolean;
  /** Stable device UUID (generated once, stored permanently). */
  deviceId: string;
}

/** Return a stable device UUID, generating and persisting one if needed. */
function getOrCreateDeviceId(): string {
  const existing = localStorage.getItem(DEVICE_KEY);
  if (existing) return existing;
  const id = uuidv4();
  localStorage.setItem(DEVICE_KEY, id);
  return id;
}

/** True if `iso` is a valid future timestamp (with expiry buffer). */
function isNotExpired(iso: string | null): boolean {
  if (!iso) return false;
  try {
    return new Date(iso).getTime() - EXPIRY_BUFFER_MS > Date.now();
  } catch {
    return false;
  }
}

export function useOsSession() {
  /** Derived snapshot — stable reference per render. */
  const getSnapshot = useCallback((): OsSessionState => {
    const token   = localStorage.getItem(TOKEN_KEY);
    const expires = localStorage.getItem(EXPIRES_KEY);
    const valid   = Boolean(token) && isNotExpired(expires);
    return {
      token:           valid ? token : null,
      isAuthenticated: valid,
      deviceId:        getOrCreateDeviceId(),
    };
  }, []);

  /** Store a new session (called after a successful POST /api/os/session). */
  const storeSession = useCallback((token: string, expiresAt: string) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(EXPIRES_KEY, expiresAt);
    // Ensure device ID is also persisted
    getOrCreateDeviceId();
  }, []);

  /** Clear the session (logout, revocation, or forced re-auth). */
  const clearSession = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EXPIRES_KEY);
    // Device ID is intentionally NOT cleared — it's a permanent device identifier.
  }, []);

  /** Whether there is a stale (expired/invalid) token in storage (for expiry message). */
  const hasStaleToken = useCallback((): boolean => {
    const token   = localStorage.getItem(TOKEN_KEY);
    const expires = localStorage.getItem(EXPIRES_KEY);
    return Boolean(token) && !isNotExpired(expires);
  }, []);

  return { getSnapshot, storeSession, clearSession, hasStaleToken };
}
